// Per-city practice history. Two backends behind one interface:
//   - authed users → server (game_runs via /api/practice/history/{id})
//   - guests       → localStorage (atlasguess.history.cities.v1)
//
// Used by ClickDistancePractice to render the past-guess panel (visit N,
// prior best, best/worst/last) and the purple history markers on the
// globe. Daily mode does not use this — its history surface is the
// share grid, not per-city stats.
//
// Guest → authed migration on signup is intentionally not implemented:
// the loss of pre-signup history is a small trade for keeping the auth
// flow simple. A future enhancement could POST the guest blob on first
// login and merge into the DB; deferred for now.

import { api, ApiError } from './api';
import { user } from './stores/auth';
import { get } from 'svelte/store';

const STORAGE_KEY = 'atlasguess.history.cities.v1';
/**
 * Cap on how many guesses we keep per city in localStorage. Prevents a
 * pathological bot from filling LocalStorage with millions of rows. The
 * authed path has no equivalent cap — game_runs is the canonical store
 * and /api/practice/history orders by played_at, so the panel can pick
 * the rows it needs from a large list cheaply.
 */
const GUEST_HISTORY_CAP = 200;

/** One past guess at a target. lat/lon may be null for legacy rows. */
export interface GuessRecord {
  /** Distance from the target in km. Same unit as game_runs.score for distance modes. */
  score_km: number;
  /** Where the player clicked, or null for pre-coord legacy rows. */
  lat: number | null;
  lon: number | null;
  /** ISO 8601 timestamp. */
  played_at: string;
}

interface ServerHistoryResponse {
  target_id: string;
  guesses: GuessRecord[];
}

/** localStorage shape: { [targetId]: GuessRecord[] }. */
type GuestHistoryBlob = Record<string, GuessRecord[]>;

/**
 * Read past guesses for the given target. Authed users hit the API;
 * guests read localStorage. Failures resolve to an empty array — a
 * blocked or corrupted store must never strand the player.
 */
export async function getHistory(targetId: string): Promise<GuessRecord[]> {
  if (get(user)) {
    try {
      const res = await api<ServerHistoryResponse>(
        `/api/practice/history/${encodeURIComponent(targetId)}`,
      );
      return res.guesses;
    } catch (err) {
      if (err instanceof ApiError) {
        console.warn('[practiceHistory] fetch failed', err.status, err.message);
      } else {
        console.warn('[practiceHistory] fetch failed', err);
      }
      return [];
    }
  }
  return readGuestHistory(targetId);
}

/**
 * Append a guess to the local store. Authed users do NOT use this for
 * persistence — the server-side game_run row written by recordRun is
 * the canonical record. Guests store everything client-side.
 *
 * Authed callers can still pass through here harmlessly (early-return)
 * to keep the call site uniform across modes.
 */
export function recordGuessLocally(targetId: string, record: GuessRecord): void {
  if (get(user)) return;
  writeGuestHistory(targetId, record);
}

/**
 * Wipe the local guest history. Currently unused by the SPA but useful
 * for an account-wipe flow / debug hook.
 */
export function clearGuestHistory(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(STORAGE_KEY);
}

function readGuestHistory(targetId: string): GuessRecord[] {
  const blob = readBlob();
  return blob[targetId] ?? [];
}

function writeGuestHistory(targetId: string, record: GuessRecord): void {
  const blob = readBlob();
  const existing = blob[targetId] ?? [];
  // Append, then trim oldest if we'd exceed the cap. New guess always
  // survives a trim — the player just made it; their best-ever stat
  // would otherwise be silently incorrect.
  const next = [...existing, record].slice(-GUEST_HISTORY_CAP);
  blob[targetId] = next;
  writeBlob(blob);
}

function readBlob(): GuestHistoryBlob {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as GuestHistoryBlob) : {};
  } catch {
    // Corrupted entry — start fresh rather than blow up the mode.
    return {};
  }
}

function writeBlob(blob: GuestHistoryBlob): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(blob));
  } catch {
    // Quota exceeded or storage disabled. Silently drop — history is
    // a UX nicety, not load-bearing.
  }
}
